import {
  IconBriefcase,
  IconCrown,
  IconShieldCheck,
  IconTool,
  IconUserQuestion,
  IconUserShield,
  IconUserStar,
} from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { RoleName } from '@/api/entities/user-role-type';

type RoleConfig = {
  labelKey: string;
  icon: TablerIcon;
  className: string;
};

export const ROLE_CONFIG: Record<RoleName, RoleConfig> = {
  [RoleName.ADMIN]: {
    labelKey: 'common:administrator',
    icon: IconShieldCheck,
    className: 'bg-rose-50 text-rose-600 border-rose-200',
  },
  [RoleName.MODERATOR]: {
    labelKey: 'common:moderator',
    icon: IconUserShield,
    className: 'bg-orange-50 text-orange-600 border-orange-200',
  },
  [RoleName.GROUP_MANAGER]: {
    labelKey: 'common:groupManager',
    icon: IconCrown,
    className: 'bg-violet-50 text-violet-600 border-violet-200',
  },
  [RoleName.COMPANY_MANAGER]: {
    labelKey: 'common:companyManager',
    icon: IconBriefcase,
    className: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  },
  [RoleName.BRANCH_MANAGER]: {
    labelKey: 'common:branchManager',
    icon: IconUserStar,
    className: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  },
  [RoleName.BRANCH_AGENT]: {
    labelKey: 'common:branchAgent',
    icon: IconTool,
    className: 'bg-sky-50 text-sky-600 border-sky-200',
  },
};

const getRoleConfig = (role?: string | null): RoleConfig | undefined =>
  role ? ROLE_CONFIG[role as RoleName] : undefined;

interface RoleIconProps {
  role?: string | null;
  size?: number;
  /** Displays the icon inside a colored round chip. */
  chip?: boolean;
  withTooltip?: boolean;
  className?: string;
}

/** Pictogram associated to a role. */
export const RoleIcon = ({
  role,
  size = 14,
  chip = true,
  withTooltip = true,
  className,
}: RoleIconProps) => {
  const { t } = useTranslation();
  const config = getRoleConfig(role);
  const Icon = config?.icon ?? IconUserQuestion;
  const label = config ? t(config.labelKey) : t('common:noRole');

  if (!chip) {
    return (
      <Icon
        size={size}
        stroke={1.9}
        className={twMerge(
          config?.className.split(' ')[1] ?? 'text-gray-500',
          className,
        )}
      />
    );
  }

  return (
    <span
      title={withTooltip ? label : undefined}
      className={twMerge(
        'inline-flex shrink-0 items-center justify-center rounded-full border',
        config?.className ?? 'bg-gray-50 text-gray-500 border-gray-200',
        className,
      )}
      style={{ width: size + 10, height: size + 10 }}
    >
      <Icon size={size} stroke={1.9} />
    </span>
  );
};

interface RoleBadgeProps {
  role?: string | null;
  /** Overrides the label (e.g. "Group manager - Lucius"). */
  label?: string | React.ReactNode;
  className?: string;
}

/** Role pictogram followed by its label. */
export const RoleBadge = ({ role, label, className }: RoleBadgeProps) => {
  const { t } = useTranslation();
  const config = getRoleConfig(role);

  return (
    <span className={twMerge('inline-flex items-center gap-1.5', className)}>
      <RoleIcon role={role} withTooltip={false} />
      <span>{label ?? (config ? t(config.labelKey) : t('common:noRole'))}</span>
    </span>
  );
};
